import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, useState} from 'react';

import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GREEN, RED} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import SecureTextInput from 'ui-library/inputs/SecureTextInput';
import TextField from 'ui-library/inputs/TextField';
import uniqueId from 'util/uniqueId';

import {
  ImportInstrumentDataModalAction,
  ImportInstrumentDataModalActionType,
} from './importInstrumentDataModalReducer';
import {ImportInstrumentDataModalState, InstrumentQueryStatus} from './InstrumentDataConstants';
import {
  createBrowseFilesInstrumentQuery,
  createValidateCredentialsInstrumentQuery,
  pollInstrumentQueryStatus,
} from './InstrumentDataDAO';

export interface InstrumentLoginPageProps {
  connectionId: string;
  dispatch: Dispatch<ImportInstrumentDataModalAction>;
  state: ImportInstrumentDataModalState;
  // File-browsing connections (e.g. Chromeleon) validate credentials by listing files (BROWSE_FILES).
  // Non-file-browsing connections (e.g. Empower) validate via a dedicated login-only VALIDATE_CREDENTIALS
  // probe, which also stores the entered credentials as a per-user secret service-side.
  useValidateCredentials?: boolean;
}

async function submitLogin(
  username: string,
  password: string,
  connectionId: string,
  useValidateCredentials: boolean
): Promise<InstrumentQueryStatus> {
  const instrumentQueryId = (
    useValidateCredentials
      ? await createValidateCredentialsInstrumentQuery(connectionId, {username, password})
      : await createBrowseFilesInstrumentQuery(connectionId, {username, password})
  ).instrumentQueryId;

  const query = await pollInstrumentQueryStatus(instrumentQueryId);
  return query.status;
}

export default function InstrumentLoginPage(props: InstrumentLoginPageProps): JSX.Element {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  return (
    <div>
      <Row>
        <Col sm={6}>
          <TextField
            name="Username"
            label="Username"
            labelStyle="label"
            isRequired={true}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
            }}
            disabled={false}
          />
        </Col>
      </Row>
      <Row className={css(styles.rowRemoveMarginBottom)}>
        <Col sm={6}>
          <FieldLabel
            htmlFor={uniqueId('fieldLabel-')}
            isRequired={true}
            label="Password "
            labelStyle="label"
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <SecureTextInput name="Password" value={password} onChange={setPassword} disabled={false} />
        </Col>
        <Col>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-right"
            size={ButtonSize.SMALL}
            text={props.state.isLoggingIn ? 'Validating credentials...' : 'Validate credentials'}
            disabled={
              props.state.loginQueryStatus === InstrumentQueryStatus.DONE ||
              props.state.isLoggingIn ||
              !(username.length > 0 && password.length > 0)
            }
            onClick={async () => {
              props.dispatch({
                type: ImportInstrumentDataModalActionType.IS_LOGGING_IN,
                isLoggingIn: true,
              });
              const loginQueryStatus = await submitLogin(
                username,
                password,
                props.connectionId,
                props.useValidateCredentials ?? false
              );
              props.dispatch({
                type: ImportInstrumentDataModalActionType.LOGIN,
                loginQueryStatus,
                loginUsername: username,
              });
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          {props.state.loginQueryStatus !== InstrumentQueryStatus.PENDING &&
            (props.state.loginQueryStatus === InstrumentQueryStatus.DONE ? (
              <div className={css(styles.validCredentials)}>Credentials entered successfully.</div>
            ) : (
              <div className={css(styles.invalidCredentials)}>Invalid username or password.</div>
            ))}
        </Col>
      </Row>
    </div>
  );
}

export const styles = StyleSheet.create({
  invalidCredentials: {
    color: RED,
    fontStyle: 'italic',
  },
  rowRemoveMarginBottom: {
    marginBottom: 0,
  },
  validCredentials: {
    color: GREEN,
    fontStyle: 'italic',
  },
});
